import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Check, Truck, MapPin, Calculator, ChevronUp, LogIn, Mail, FileText } from 'lucide-react';
import { internalLink } from '../lib/utils';
import { QuickContactPanel, TopMailLink } from './TopContact';

const TOPBAR_HEIGHT = 36; // px — top bilgi barı yüksekliği

const Header = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [activeSubmenu, setActiveSubmenu] = useState(null);
    const [megaMenuOpen, setMegaMenuOpen] = useState(false);
    const [sectorenOpen, setSectorenOpen] = useState(false);
    const [showScrollTop, setShowScrollTop] = useState(false);
    const [quoteOpen, setQuoteOpen] = useState(false);
    const megaRef = useRef(null);
    const sectorenRef = useRef(null);
    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => {
            const y = window.scrollY;
            setScrolled(y > TOPBAR_HEIGHT);
            setShowScrollTop(y > 500);
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        const handleClickOutside = (e) => {
            if (megaRef.current && !megaRef.current.contains(e.target)) setMegaMenuOpen(false);
            if (sectorenRef.current && !sectorenRef.current.contains(e.target)) setSectorenOpen(false);
        };
        window.addEventListener('mousedown', handleClickOutside);
        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        setIsMenuOpen(false);
        setActiveSubmenu(null);
        setMegaMenuOpen(false);
        setSectorenOpen(false);
        setQuoteOpen(false);
    }, [location]);

    // Mobil menu açıkken body scroll'u kapat
    useEffect(() => {
        if (isMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [isMenuOpen]);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // "Offerte" bovenaan: opent het snelle formulier onder de header. Op / (hero-kaart), /offerte/ en
    // /contact/ staat al een volledig formulier — daar scrollen we ernaartoe i.p.v.
    // een tweede formulier op dezelfde pagina te openen.
    const onQuoteClick = () => {
        const pageForm = /^\/((offerte|contact)\/?)?$/.test(location.pathname) && document.querySelector('main form');
        if (pageForm) {
            setQuoteOpen(false);
            pageForm.scrollIntoView({ behavior: 'smooth', block: 'start' });
            const first = pageForm.querySelector('input, textarea, select');
            if (first) first.focus({ preventScroll: true });
            return;
        }
        setIsMenuOpen(false);
        setQuoteOpen((v) => !v);
    };

    const solutions = [
        { name: 'GPS Tracking', desc: 'GPS tracker voor bedrijfswagen & vloot', href: '/gps-tracking/', icon: MapPin },
        { name: 'Fleet Management', desc: 'Volledig wagenpark overzicht', href: '/fleet-management/', icon: Truck },
        { name: 'Rittenregistratie', desc: 'Fiscaal-proof rittenadministratie', href: '/rittenregistratie/', icon: MapPin },
    ];

    const sectoren = [
        { name: 'Transport & Logistiek', href: '/fleet-management/transport/' },
        { name: 'Koeriersdiensten', href: '/fleet-management/koeriers/' },
        { name: 'Bouwsector', href: '/rittenregistratie/bouw/' },
        { name: 'Taxi & VTC', href: '/rittenregistratie/taxi/' },
        { name: 'Installateurs', href: '/rittenregistratie/installateurs/' },
        { name: 'Servicebedrijven', href: '/fleet-management/service/' },
        { name: 'Thuiszorg', href: '/fleet-management/thuiszorg/' },
        { name: 'Landbouw & Agri', href: '/fleet-management/landbouw/' },
        { name: 'Gemeenten & Overheid', href: '/fleet-management/overheid/' },
        { name: 'Schoonmaakbedrijven', href: '/fleet-management/schoonmaak/' },
        { name: 'Hoveniers & Groenvoorziening', href: '/fleet-management/hoveniers/' },
        { name: 'Verhuisbedrijven', href: '/fleet-management/verhuizers/' },
        { name: 'Afvalbeheer & Recycling', href: '/fleet-management/afvalbeheer/' },
        { name: 'Catering & Horeca', href: '/fleet-management/catering/' },
        { name: 'Vastgoed & Makelaars', href: '/rittenregistratie/vastgoed/' },
        { name: 'Farmaceutisch Transport', href: '/fleet-management/farmaceutisch/' },
    ];

    const isActive = (path) => {
        if (path === '/') return location.pathname === '/';
        return location.pathname.startsWith(path);
    };

    const navLinkClass = (path) =>
        `px-3 py-2 text-sm font-semibold transition-colors rounded-lg ${
            isActive(path)
                ? 'text-blue-700 bg-blue-50'
                : 'text-slate-700 hover:text-blue-700 hover:bg-slate-50'
        }`;

    return (
        <>
            {/* Top Bar — altı çizili değil, yükseklik sabit */}
            <div
                className="bg-cyan-50 border-b border-cyan-100 text-cyan-900 text-xs py-2.5 hidden md:block z-[60] relative transition-transform duration-300"
                style={{
                    height: TOPBAR_HEIGHT,
                    transform: scrolled ? `translateY(-${TOPBAR_HEIGHT}px)` : 'translateY(0)',
                    position: 'sticky',
                    top: 0,
                }}
                aria-hidden={scrolled}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
                    <div className="flex items-center space-x-5">
                        <span className="flex items-center text-cyan-800">
                            <Check className="h-3 w-3 mr-1 text-emerald-600" /> Hardware inbegrepen
                        </span>
                        <span className="text-cyan-300 hidden lg:inline">|</span>
                        <span className="hidden lg:flex items-center text-cyan-800">
                            <Check className="h-3 w-3 mr-1 text-emerald-600" /> Maandelijks opzegbaar
                        </span>
                        <span className="text-cyan-300 hidden lg:inline">|</span>
                        <span className="hidden lg:flex items-center font-semibold text-cyan-950">
                            <Check className="h-3 w-3 mr-1 text-emerald-600" /> Belastingdienst-proof rittenregistratie
                        </span>
                        <span className="text-cyan-300 hidden xl:inline">|</span>
                        <span className="hidden xl:flex items-center text-cyan-800">
                            <Check className="h-3 w-3 mr-1 text-emerald-600" /> Zelf geïnstalleerd in enkele minuten
                        </span>
                    </div>
                </div>
            </div>

            {/* Main Header — sticky, altına içerik kaymaması için spacer App'e eklendi */}
            <header
                className="sticky top-0 w-full z-50 transition-all duration-300 bg-white border-b border-slate-100"
                style={{
                    boxShadow: scrolled ? '0 1px 20px rgba(0,0,0,0.08)' : '0 1px 0 rgba(0,0,0,0.05)',
                }}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        {/* Logo */}
                        <div className="flex-shrink-0 flex items-center">
                            <Link to="/" className="flex items-center" aria-label="FleetTrack Holland — Home">
                                <picture>
                                    <source srcSet="/logo-header.webp" type="image/webp" />
                                    <img
                                        src="/logo-header.png"
                                        alt="FleetTrack Holland logo"
                                        className="h-8 sm:h-10 w-auto"
                                        width="160"
                                        height="40"
                                        loading="eager"
                                        fetchPriority="high"
                                    />
                                </picture>
                            </Link>
                        </div>

                        {/* Desktop Navigation */}
                        <nav className="hidden lg:flex items-center space-x-1" aria-label="Hoofdnavigatie">
                            <Link to="/" className={navLinkClass('/')} aria-current={location.pathname === '/' ? 'page' : undefined}>
                                Home
                            </Link>

                            {/* Oplossingen Mega Menu */}
                            <div className="relative" ref={megaRef}>
                                <button
                                    onClick={() => { setMegaMenuOpen(!megaMenuOpen); setSectorenOpen(false); }}
                                    className={`flex items-center px-3 py-2 text-sm font-semibold transition-colors rounded-lg ${megaMenuOpen ? 'text-blue-700 bg-blue-50' : 'text-slate-700 hover:text-blue-700 hover:bg-slate-50'}`}
                                    aria-expanded={megaMenuOpen}
                                    aria-haspopup="true"
                                    id="oplossingen-menu-btn"
                                >
                                    Oplossingen <ChevronDown className={`ml-1 h-4 w-4 transition-transform duration-200 ${megaMenuOpen ? 'rotate-180' : ''}`} />
                                </button>
                                {megaMenuOpen && (
                                    <div
                                        className="absolute left-1/2 -translate-x-1/2 mt-2 w-[600px] bg-white rounded-2xl shadow-2xl border border-slate-100 p-6 z-50 animate-fade-in"
                                        role="menu"
                                    >
                                        <div className="grid grid-cols-2 gap-2">
                                            {solutions.map((sol) => (
                                                <Link
                                                    key={sol.name}
                                                    to={internalLink(sol.href)}
                                                    className="flex items-start p-3 rounded-xl hover:bg-blue-50 transition-colors group"
                                                    role="menuitem"
                                                >
                                                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0 group-hover:bg-blue-200 transition-colors">
                                                        <sol.icon className="h-5 w-5 text-blue-600" aria-hidden="true" />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-bold text-slate-900 group-hover:text-blue-700">{sol.name}</p>
                                                        <p className="text-xs text-slate-500 mt-0.5">{sol.desc}</p>
                                                    </div>
                                                </Link>
                                            ))}
                                        </div>
                                        <div className="mt-4 pt-4 border-t border-slate-100">
                                            <Link
                                                to="/besparingscalculator/"
                                                className="flex items-center justify-center p-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors font-bold text-sm"
                                                role="menuitem"
                                            >
                                                <Calculator className="h-4 w-4 mr-2" aria-hidden="true" />
                                                Bereken uw besparing met de ROI Calculator →
                                            </Link>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Sectoren */}
                            <div className="relative" ref={sectorenRef}>
                                <button
                                    onClick={() => { setSectorenOpen(!sectorenOpen); setMegaMenuOpen(false); }}
                                    className={`flex items-center px-3 py-2 text-sm font-semibold transition-colors rounded-lg ${sectorenOpen ? 'text-blue-700 bg-blue-50' : 'text-slate-700 hover:text-blue-700 hover:bg-slate-50'}`}
                                    aria-expanded={sectorenOpen}
                                    aria-haspopup="true"
                                    id="sectoren-menu-btn"
                                >
                                    Sectoren <ChevronDown className={`ml-1 h-4 w-4 transition-transform duration-200 ${sectorenOpen ? 'rotate-180' : ''}`} />
                                </button>
                                {sectorenOpen && (
                                    <div
                                        className="absolute left-0 mt-2 w-[480px] bg-white rounded-xl shadow-2xl border border-slate-100 p-4 z-50 animate-fade-in"
                                        role="menu"
                                    >
                                        <div className="grid grid-cols-2 gap-1">
                                        {sectoren.map((sec) => (
                                            <Link
                                                key={sec.name}
                                                to={internalLink(sec.href)}
                                                className="block px-3 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-700 transition-colors font-medium rounded-lg"
                                                role="menuitem"
                                            >
                                                {sec.name}
                                            </Link>
                                        ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <Link to="/prijzen/" className={navLinkClass('/prijzen')}>Prijzen</Link>
                            <Link to="/over-ons/" className={`${navLinkClass('/over-ons')} whitespace-nowrap`}>Over Ons</Link>
                            <Link to="/blog/" className={navLinkClass('/blog')}>Blog</Link>
                        </nav>

                        {/* Right side */}
                        <div className="hidden lg:flex items-center space-x-2 xl:space-x-3">
                            {/* E-mail */}
                            <TopMailLink
                                id="header-mail-btn"
                                className="inline-flex items-center justify-center w-10 h-10 rounded-lg border border-slate-200 text-slate-700 hover:text-blue-700 hover:bg-slate-50 transition-colors"
                            >
                                <Mail className="h-5 w-5" aria-hidden="true" />
                            </TopMailLink>

                            {/* Portal login */}
                            <a
                                href="https://portal.fleettrackholland.nl"
                                className="inline-flex items-center px-3 py-2 text-sm font-semibold text-slate-700 hover:text-blue-700 hover:bg-slate-50 rounded-lg transition-colors"
                                id="header-portal-login"
                                aria-label="Inloggen"
                            >
                                <LogIn className="h-4 w-4 xl:mr-1.5" aria-hidden="true" />
                                <span className="hidden xl:inline">Inloggen</span>
                            </a>

                            {/* CTA — opent het snelle offerteformulier (zie QuickContactPanel) */}
                            <button
                                type="button"
                                onClick={onQuoteClick}
                                aria-expanded={quoteOpen}
                                aria-controls="header-quick-form"
                                className="inline-flex items-center px-5 py-2.5 text-sm font-bold rounded-lg text-white bg-orange-500 hover:bg-orange-600 shadow-sm transition-all active:scale-95"
                                id="header-cta-btn"
                            >
                                Offerte Aanvragen
                            </button>
                        </div>

                        {/* Mobile toggle */}
                        <div className="lg:hidden flex items-center space-x-1.5">
                            {/* Contact altijd zichtbaar, ook zonder menu: Offerte / Mail */}
                            <button
                                type="button"
                                onClick={onQuoteClick}
                                aria-expanded={quoteOpen}
                                aria-controls="header-quick-form"
                                aria-label="Offerte aanvragen"
                                id="header-cta-btn-mobile"
                                className="inline-flex items-center justify-center w-10 h-10 rounded-lg text-white bg-orange-500 active:scale-95 transition-transform"
                            >
                                <FileText className="h-5 w-5" aria-hidden="true" />
                            </button>
                            <TopMailLink
                                id="header-mail-btn-mobile"
                                className="inline-flex items-center justify-center w-10 h-10 rounded-lg border border-slate-200 text-slate-700 active:scale-95 transition-transform"
                            >
                                <Mail className="h-5 w-5" aria-hidden="true" />
                            </TopMailLink>
                            <button
                                onClick={() => setIsMenuOpen(!isMenuOpen)}
                                className="p-1.5 rounded-md text-slate-700"
                                aria-expanded={isMenuOpen}
                                aria-controls="mobile-menu"
                                aria-label={isMenuOpen ? 'Menu sluiten' : 'Menu openen'}
                            >
                                {isMenuOpen ? <X className="h-8 w-8" aria-hidden="true" /> : <Menu className="h-8 w-8" aria-hidden="true" />}
                            </button>
                        </div>
                    </div>
                </div>

                <QuickContactPanel open={quoteOpen} onClose={() => setQuoteOpen(false)} />

                {/* Mobile Menu */}
                <div
                    id="mobile-menu"
                    className={`fixed inset-0 z-50 bg-white transform transition-transform duration-300 ease-in-out lg:hidden ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}
                    aria-hidden={!isMenuOpen}
                    inert={!isMenuOpen}
                >
                    {/* Mobile header bar */}
                    <div className="flex items-center justify-between px-4 h-16 border-b border-slate-100">
                        <Link to="/" onClick={() => setIsMenuOpen(false)}>
                            <img src="/logo-menu.webp" alt="FleetTrack Holland" className="h-9 w-auto" width="400" height="103" loading="lazy" />
                        </Link>
                        <button
                            onClick={() => setIsMenuOpen(false)}
                            className="p-2 rounded-md text-slate-700"
                            aria-label="Menu sluiten"
                        >
                            <X className="h-8 w-8" aria-hidden="true" />
                        </button>
                    </div>
                    <div className="flex flex-col h-[calc(100%-64px)] overflow-y-auto pb-12 px-6 pt-4">
                        <div className="flex flex-col space-y-1 mb-8">
                            <Link to="/" className="px-4 py-3 text-lg font-bold text-slate-900 rounded-xl hover:bg-slate-50">Home</Link>

                            {/* Mobile Oplossingen */}
                            <div>
                                <button
                                    onClick={() => setActiveSubmenu(activeSubmenu === 'oplossingen' ? null : 'oplossingen')}
                                    className="flex items-center justify-between w-full px-4 py-3 text-lg font-bold text-slate-900 rounded-xl hover:bg-slate-50"
                                    aria-expanded={activeSubmenu === 'oplossingen'}
                                >
                                    Oplossingen <ChevronDown className={`h-5 w-5 transition-transform ${activeSubmenu === 'oplossingen' ? 'rotate-180 text-blue-600' : ''}`} aria-hidden="true" />
                                </button>
                                <div className={`overflow-hidden transition-all duration-300 ${activeSubmenu === 'oplossingen' ? 'max-h-96 pl-4' : 'max-h-0'}`}>
                                    {solutions.map((sol) => (
                                        <Link key={sol.name} to={internalLink(sol.href)} className="flex items-center px-4 py-2.5 text-slate-600 font-medium hover:text-blue-600">
                                            <sol.icon className="h-4 w-4 mr-2 text-blue-500" aria-hidden="true" />
                                            {sol.name}
                                        </Link>
                                    ))}
                                </div>
                            </div>

                            {/* Mobile Sectoren */}
                            <div>
                                <button
                                    onClick={() => setActiveSubmenu(activeSubmenu === 'sectoren' ? null : 'sectoren')}
                                    className="flex items-center justify-between w-full px-4 py-3 text-lg font-bold text-slate-900 rounded-xl hover:bg-slate-50"
                                    aria-expanded={activeSubmenu === 'sectoren'}
                                >
                                    Sectoren <ChevronDown className={`h-5 w-5 transition-transform ${activeSubmenu === 'sectoren' ? 'rotate-180 text-blue-600' : ''}`} aria-hidden="true" />
                                </button>
                                <div className={`overflow-hidden transition-all duration-300 ${activeSubmenu === 'sectoren' ? 'max-h-96 pl-4' : 'max-h-0'}`}>
                                    {sectoren.map((sec) => (
                                        <Link key={sec.name} to={internalLink(sec.href)} className="block px-4 py-2.5 text-slate-600 font-medium hover:text-blue-600">{sec.name}</Link>
                                    ))}
                                </div>
                            </div>

                            <Link to="/prijzen/" className="px-4 py-3 text-lg font-bold text-slate-900 rounded-xl hover:bg-slate-50">Prijzen</Link>
                            <Link to="/blog/" className="px-4 py-3 text-lg font-bold text-slate-900 rounded-xl hover:bg-slate-50">Blog</Link>
                            <Link to="/over-ons/" className="px-4 py-3 text-lg font-bold text-slate-900 rounded-xl hover:bg-slate-50">Over Ons</Link>
                        </div>

                        <div className="flex flex-col space-y-4 mt-auto">
                            <a
                                href="https://portal.fleettrackholland.nl"
                                className="w-full py-4 text-center text-lg font-bold rounded-2xl border-2 border-slate-200 text-slate-700"
                                id="mobile-portal-login"
                            >
                                Inloggen op klantenportaal
                            </a>
                            <Link to="/offerte/" className="w-full py-4 text-center text-lg font-bold rounded-2xl text-white bg-orange-500 shadow-xl active:scale-95 transition-transform">
                                Offerte Aanvragen
                            </Link>
                        </div>
                    </div>
                </div>
            </header>

            {/* Scroll to top button */}
            {showScrollTop && (
                <button
                    onClick={scrollToTop}
                    className="scroll-to-top"
                    aria-label="Terug naar boven"
                    id="scroll-to-top-btn"
                >
                    <ChevronUp className="h-5 w-5" aria-hidden="true" />
                </button>
            )}
        </>
    );
};

export default Header;
